/** returns the delay in milliseconds before the given attempt (1-indexed) */
type BackoffStrategy = (attempt: number) => number

export interface RetryPolicy {
  maxRetries?: number
  backoff?: BackoffStrategy
  retryCondition?: (error: any) => boolean
}

interface RetryExecutor {
  run<T>(fn: () => PromiseLike<T>): Promise<T>
}

interface JitterOptions {
  min?: number
  max?: number
}

function applyJitter(delay: number, jitter?: JitterOptions | false): number {
  if (jitter === false) {
    return delay
  }
  const min = jitter?.min ?? 0.75
  const max = jitter?.max ?? 1.0
  return Math.floor(delay * (min + Math.random() * (max - min)))
}

interface LinearBackoffOptions {
  baseDelayMs?: number
  incrementMs?: number
  maxDelayMs?: number
  jitter?: JitterOptions | false
}

export function linearBackoff(opts: LinearBackoffOptions = {}): BackoffStrategy {
  const base = opts.baseDelayMs ?? 500
  const increment = opts.incrementMs ?? base
  const max = opts.maxDelayMs ?? 10_000
  return (attempt) => applyJitter(Math.min(base + increment * (attempt - 1), max), opts.jitter)
}

interface ExponentialBackoffOptions {
  baseDelayMs?: number
  factor?: number
  maxDelayMs?: number
  jitter?: JitterOptions | false
}

export function exponentialBackoff(opts: ExponentialBackoffOptions = {}): BackoffStrategy {
  const base = opts.baseDelayMs ?? 500
  const factor = opts.factor ?? 2
  const max = opts.maxDelayMs ?? 10_000
  return (attempt) => applyJitter(Math.min(base * Math.pow(factor, attempt - 1), max), opts.jitter)
}

/**
 * Returns true for network failures and 5xx server errors. Checks both error.status (fetch) and
 * error.statusCode (mobile ApiError).
 */
function isTransientError(error: any): boolean {
  if (error?.statusText === 'abort' || error?.name === 'AbortError') {
    return false
  }
  const status = error?.status ?? error?.statusCode
  return !status || status >= 500
}

/**
 * Creates a retry executor for the given policy. Call .run() to execute a function with the
 * configured retry behavior.
 *
 * @example
 * withRetry().run(() => fetch(url))
 *
 * @example
 * withRetry({ maxRetries: 3, backoff: linearBackoff() }).run(() => fetch(url))
 */
export function withRetry(opts: RetryPolicy = {}): RetryExecutor {
  const maxRetries = opts.maxRetries ?? 5
  const backoff = opts.backoff ?? exponentialBackoff()
  const condition = opts.retryCondition ?? isTransientError

  return {
    async run<T>(fn: () => PromiseLike<T>): Promise<T> {
      let lastError: any
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          return await fn()
        } catch (error) {
          lastError = error
          if (attempt >= maxRetries || !condition(error)) {
            throw error
          }
          await new Promise<void>((resolve) => setTimeout(resolve, backoff(attempt + 1)))
        }
      }
      throw lastError
    },
  }
}
